import { BookingView, RollingAppointmentView } from "@/api/types";

/**
 * The one renderer for "who booked" across the app. Ad-hoc pages and rolling links return
 * different DTOs that carry the same handful of fields the host actually reads, so both are
 * normalised to {@link BookingRow} once and drawn the same way afterwards.
 *
 * <p>Renders only the list; the caller keeps its own wrapper, so the component never has to know
 * what it is nested inside.</p>
 */

export function whenText(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export interface BookingRow {
  id: string;
  start: string;
  /** When it was booked, not when the slot is. This is what the list is ordered by. */
  createdAt: string;
  inviteeName: string | null;
  inviteeEmail: string;
  cancelled: boolean;
  /** Ad-hoc only: the confirmation hard-bounced, so the slot was released. */
  bounced: boolean;
  meetingUrl: string | null;
}

export function bookingToRow(b: BookingView): BookingRow {
  return {
    id: b.id,
    start: b.start,
    createdAt: b.created_at,
    inviteeName: b.invitee_name,
    inviteeEmail: b.invitee_email,
    cancelled: b.status === "CANCELLED",
    bounced: b.status === "BOUNCED",
    meetingUrl: b.meeting_url,
  };
}

export function appointmentToRow(a: RollingAppointmentView): BookingRow {
  return {
    id: a.id,
    start: a.start,
    createdAt: a.created_at,
    inviteeName: a.invitee_name,
    inviteeEmail: a.invitee_email,
    cancelled: a.status === "CANCELLED",
    bounced: false,
    meetingUrl: a.meeting_url,
  };
}

/**
 * Most recently booked first, matching the event journal's order. Ordering by slot time instead
 * would put the furthest-out appointment on top of a long-running rolling link, and would make
 * the list's cap show the wrong end of it.
 */
export function sortByBookedAtDesc(rows: BookingRow[]): BookingRow[] {
  return [...rows].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

export function BookingRows({ rows }: { rows: BookingRow[] }) {
  return (
    <ul className="space-y-1.5 text-xs">
      {rows.map((r) => (
        <li key={r.id} className="flex flex-col leading-tight">
          <span className="font-medium">{whenText(r.start)}</span>
          <span className="text-muted-foreground">
            {r.inviteeName ?? "—"} · {r.inviteeEmail}
          </span>
          {r.meetingUrl && (
            <a
              href={r.meetingUrl}
              target="_blank"
              rel="noreferrer"
              className="font-medium text-brand-blue hover:underline"
            >
              Join link
            </a>
          )}
          {r.cancelled && <span className="text-red-600">cancelled</span>}
          {r.bounced && <span className="text-amber-600">bounced</span>}
        </li>
      ))}
    </ul>
  );
}
